"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { errMsg, listVoices, deleteVoice, type VoiceSummary } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DeleteConfirmDialog } from "@/components/delete-confirm-dialog";
import { RequireRole } from "@/components/auth/require-role";

export default function VoicesPage() {
  const [voices, setVoices] = useState<VoiceSummary[]>([]);
  const [canCreate, setCanCreate] = useState(false);
  const [provider, setProvider] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    listVoices().then((res) => {
      setVoices(res.voices);
      setCanCreate(res.can_create);
      setProvider(res.provider);
    }).catch((e) => setError(errMsg(e)));
  }, []);

  async function handleDelete(id: string) {
    await deleteVoice(id);
    setVoices((prev) => prev.filter((v) => v.id !== id));
  }

  return (
    <div className="flex min-h-full flex-col p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">声音</h1>
        {canCreate && <RequireRole roles={["owner"]}>
          <Button asChild size="sm">
            <Link href="/voices/create">创建</Link>
          </Button>
        </RequireRole>}
      </div>
      {provider === "tencent" && (
        <p className="mb-4 text-sm text-muted-foreground">
          当前使用腾讯 TTS 内置音色，可直接试听和选择，无需上传录音。音色列表由服务器配置管理。
        </p>
      )}
      {error && <p role="alert" className="mb-4 text-sm text-destructive">{error}</p>}

      {voices.length === 0 ? (
        <div className="flex flex-1 items-center justify-center text-muted-foreground">
          暂无声音。
          {canCreate && <RequireRole roles={["owner"]}>
            {" "}
            <Link href="/voices/create" className="ml-1 underline">
              立即创建
            </Link>
          </RequireRole>}
        </div>
      ) : (
        <ul className="space-y-2">
          {voices.map((v) => (
            <li key={v.id} className="flex items-center gap-2">
              <Link
                href={`/voices/${v.id}`}
                className="block flex-1 rounded-lg border px-4 py-3 transition-colors hover:bg-muted"
              >
                <span className="font-medium">{v.name}</span>
                <span className="ml-2 text-sm text-muted-foreground">{v.role}</span>
              </Link>
              {!v.builtin && <RequireRole roles={["owner"]}>
                <DeleteConfirmDialog
                  title={`确认删除声音“${v.name}”？`}
                  description="此操作将永久删除声音及其参考音频，且无法撤销。"
                  onConfirm={() => handleDelete(v.id)}
                />
              </RequireRole>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
